import { FC, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";

import { TaskDriverStatusEnum } from "@/domain/models/common";
import { JunkLoadStatusEnum, ServicesType } from "@/domain/models/job";
import { useTranslator } from "@/i18n/useTranslator";

import { DriverDashboardSection, DriverFlowBottomButton } from "../blocks";
import { DsDataDisplay, DsDivider, DsText } from "../components/ui";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import {
  useCompleteTaskMutation,
  useUpdateTaskMutation,
} from "../data/mutations/taskMutations";
import { useJobQuery } from "../data/queries/jobQueries";
import {
  ServiceQueryKeys,
  useServiceQuery,
} from "../data/queries/serviceQueries";
import {
  TaskQueryKeys,
  useTaskQuery,
  useTasksToDumpQuery,
} from "../data/queries/taskQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { isDriverReviewingTaskAtom } from "../stores/atoms";
import { setJunkLoadInProgress } from "../utils/service";
import { DriverTaskPicture } from "./DriverTaskPicture";

type DriverDumpTasksReviewPropsType = JobServiceTaskBaseType & {
  onBottomButtonPress?: () => void;
};

const DriverDumpTasksReview: FC<DriverDumpTasksReviewPropsType> = ({
  jobId,
  serviceId,
  taskId,
  onBottomButtonPress,
}) => {
  const { t } = useTranslator();

  const setIsDriverReviewingTask = useSetAtom(isDriverReviewingTaskAtom);

  const { mutateAsync: completeTask, isPending: isCompletingTask } =
    useCompleteTaskMutation();

  const { data: jobData } = useJobQuery({ jobId });
  const { data: taskData } = useTaskQuery({ jobId, serviceId, taskId });
  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const { data: tasksToDump } = useTasksToDumpQuery();

  const { mutateAsync: updateTask, variables: updateTaskVariables } =
    useUpdateTaskMutation();

  const queryClient = useQueryClient();

  const { mutateAsync: updateService } = useUpdateServiceMutation();

  const isDrivingPerformingMultipleLoads = Boolean(
    serviceData?.junkLoads?.length,
  );

  useEffect(() => {
    setIsDriverReviewingTask(true);
  }, [setIsDriverReviewingTask]);

  if (!jobData || !taskData) {
    return null;
  }

  const onCompleteJob = async () => {
    // If driver is performing multiple loads, just complete the current load and get back to the site
    // An invalid cleanedAreasPhotos means the driver didn't finish the cleanup yet, so it's filling another load
    if (
      isDrivingPerformingMultipleLoads &&
      !serviceData?.cleanedAreasPhotos?.length &&
      serviceData?.junkLoads
    ) {
      const lastJunkLoadIndex = serviceData.junkLoads.length - 1;
      const updatedJunkLoad = serviceData?.junkLoads?.[lastJunkLoadIndex];

      if (updatedJunkLoad) {
        updatedJunkLoad.status = JunkLoadStatusEnum.COMPLETED;

        serviceData?.junkLoads?.splice(lastJunkLoadIndex, 1, updatedJunkLoad);

        await updateService({
          jobId,
          serviceId,
          junkLoads: [...serviceData.junkLoads],
          // Reset values to get back to the site
          driverFlowStatus: TaskDriverStatusEnum.DRIVING_TO_SITE,
          needToDump: false,
          hasDumped: false,
        });

        await updateTask({
          jobId,
          serviceId,
          taskId,
          driverFlowStatus: TaskDriverStatusEnum.DRIVING_TO_SITE,
          needToDump: false,
          hasDumped: false,
        });

        await queryClient.invalidateQueries({ queryKey: TaskQueryKeys.all });
        await queryClient.invalidateQueries({
          queryKey: ServiceQueryKeys.all,
        });
      }
    } else {
      const promises = tasksToDump?.map(task => {
        return completeTask({
          jobId: task.jobId,
          serviceId: task.serviceId,
          taskId: task.taskId,
        });
      });

      await Promise.allSettled(promises ?? []);
    }

    setIsDriverReviewingTask(false);

    onBottomButtonPress?.();
  };

  const goBackTo = async (driverFlowStatus: TaskDriverStatusEnum) => {
    const servicesParams: Partial<ServicesType> = {};

    // If driver is performing multiple loads, set the last load to in progress
    if (isDrivingPerformingMultipleLoads && serviceData?.junkLoads) {
      const updatedJunkLoads = setJunkLoadInProgress(
        serviceData,
        serviceData.junkLoads.length - 1,
      );

      if (updatedJunkLoads) {
        servicesParams.junkLoads = updatedJunkLoads;
      }
    }

    await updateService({
      jobId,
      serviceId,
      driverFlowStatus,
      ...servicesParams,
    });

    await updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus,
    });
  };

  // If driver is performing many loads to finish cleanup, need to get data from the last load
  const baseData = isDrivingPerformingMultipleLoads
    ? serviceData?.junkLoads?.[serviceData.junkLoads.length - 1]
    : serviceData;

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex flex-grow w-full flex-col">
        <DsText variant="pageTitle">
          {t(
            isDrivingPerformingMultipleLoads
              ? "driver_dump_tasks_review_screen.return_to_site_title"
              : "driver_task_review_screen.title",
          )}
        </DsText>
        <DsDivider className="my-4" />

        {/* Tasks to dump */}
        <DriverDashboardSection
          title="driver_dashboard.jobs_dumped"
          variant="dataDisplayTitle"
          titleClassName="mb-0"
          tasks={tasksToDump ?? []}
          shouldBlockTaskClick
          shouldApplyDisabledStyles={false}
          shouldShowInProgressBadge={false}
        />

        <DsDivider className="mt-4" />

        <DsDataDisplay
          title="driver_dump_tasks_review_screen.surcharge"
          dataClassName="font-bold"
          data={baseData?.weightSurcharge?.toString()}
          onEditClick={() => {
            void goBackTo(TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE);
          }}
          isLoading={Boolean(
            updateTaskVariables?.driverFlowStatus ===
              TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE,
          )}
        />

        <DsDataDisplay
          title="driver_dump_tasks_review_screen.dump_receipt"
          dataClassName="flex"
          data={
            <DriverTaskPicture
              field="dumpReceiptPhotoUrl"
              jobId={jobId}
              serviceId={serviceId}
              junkLoadIndex={
                isDrivingPerformingMultipleLoads
                  ? Number(serviceData?.junkLoads?.length ?? 0) - 1
                  : null
              }
            />
          }
        />
      </div>
      <DriverFlowBottomButton
        nextButtonTitle={
          isDrivingPerformingMultipleLoads
            ? "done"
            : "driver_task_review_screen.complete_job"
        }
        isNextButtonLoading={isCompletingTask}
        onNextButtonClick={() => {
          void onCompleteJob();
        }}
      />
    </div>
  );
};

export { DriverDumpTasksReview };
